import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react'

/**
 * The longest a tab holds the selection without the route confirming it.
 *
 * A safety net, nothing more: a navigation that lands clears the hold immediately, and one
 * that never lands must not leave the bar pointing at a page the app is not on.
 */
const MAX_HOLD_MS = 2500

interface IPendingTab {
  /** The tab that should read as selected right now, or null to trust the route. */
  pendingKey: string | null
  /** Goes on each tab's `onClick`; the tab names itself with `data-nav-key`. */
  markPending: (event: MouseEvent<HTMLElement>) => void
}

/**
 * Move the selection to the tab that was tapped, rather than when its page arrives.
 *
 * Every route in this app is `lazy()`, and react-router runs the navigation inside a React
 * transition — which by design keeps the *old* screen up, chunk fetch and all, and flips
 * `NavLink`'s `isActive` only once the new one is ready to commit. On a phone that is most
 * of a second during which a tapped tab shows nothing at all having happened, and no press
 * feedback survives that long.
 *
 * So the tap claims the selection and the route confirms it. Keying the release on
 * `pathname` also covers a navigation that lands somewhere else — a guard, or a redirect.
 */
export const usePendingTab = (pathname: string): IPendingTab => {
  const [pendingKey, setPendingKey] = useState<string | null>(null)
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const release = useCallback(() => {
    if (holdTimer.current) {
      clearTimeout(holdTimer.current)
      holdTimer.current = null
    }
    setPendingKey(null)
  }, [])

  const markPending = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      release()
      setPendingKey(event.currentTarget.dataset.navKey ?? null)
      holdTimer.current = setTimeout(release, MAX_HOLD_MS)
    },
    [release],
  )

  useEffect(() => {
    release()

    return () => {
      if (holdTimer.current) clearTimeout(holdTimer.current)
    }
  }, [pathname, release])

  return { pendingKey, markPending }
}

export default usePendingTab
